/**
 * Agent Capability Package Definitions
 *
 * Capability packages are curated bundles of tools that enable specific business
 * functions on an agent. Users toggle packages on/off in the agent editor UI —
 * they never see individual tools.
 *
 * Architecture:
 *   Package (toggle on/off) → Tier (opt-in for higher-risk features) → Tool (invisible to user)
 *
 * Configuration is stored in `metadata.capabilities` on the agent entity.
 * This is separate from the legacy `metadata.toolGroups` array (which continues
 * working for REPORTING and API_ACTIONS).
 *
 * Each package maps to a ToolGroup enum value. Tools within a package are either
 * "infrastructure" (always included when the package is on) or assigned to a
 * specific tier that can be individually enabled/disabled.
 */

import { ToolGroup } from '../enums/tool-group.enum';
import { RbacActions } from './rbac-actions';

// ── Tier Definition ─────────────────────────────────────────────────

export interface CapabilityTierDefinition {
	/** Machine key used in metadata and capabilityTier on tools (e.g., 'lookups') */
	key: string;
	/** Display label for the UI */
	label: string;
	/** Short description shown under the toggle */
	description: string;
	/** Whether this tier is enabled by default when the package is toggled on */
	enabledByDefault: boolean;
	/**
	 * Behavioural guidance appended to the agent's system prompt only when THIS
	 * tier is enabled (in addition to any package-level addendum). Use for rules
	 * that only apply to a higher-risk tier — e.g. the payment-confirmation flow
	 * for "Make payments". Keep it short; it costs prompt budget.
	 */
	systemPromptAddendum?: string;
}

// ── Package Definition ──────────────────────────────────────────────

export interface CapabilityPackageDefinition {
	/** Machine key matching ToolGroup enum value (e.g., 'external_client_service') */
	key: ToolGroup;
	/** Display label for the UI (e.g., 'Customer Account Service') */
	label: string;
	/** Package-level description shown in the editor */
	description: string;
	/** FontAwesome icon name for the UI card (e.g., 'headset') */
	faIcon: string;
	/**
	 * Which kinds of agent may be ASSIGNED this group — the two-way gate.
	 *   - 'user'   — offered to user-created agents in the agent builder
	 *   - 'system' — reserved for code-defined system agents (e.g. Sales Helper);
	 *                hidden from the builder and rejected for user agents at runtime
	 *   - 'both'   — assignable to either
	 * Filters which groups the builder shows AND blocks a non-system agent from
	 * invoking a system-only group's tools (prevents accidental tool leaks).
	 */
	assignableTo: 'user' | 'system' | 'both';
	/**
	 * Optional RBAC action the CURRENT USER must hold to use any tool in this
	 * group. When set, the group's tools are neither advertised to nor executable
	 * by a user lacking the action — a categorical, per-user gate layered on top
	 * of group assignment. Use for groups whose tools reach data beyond the
	 * user's normal scope (e.g. REPORTING's raw-SQL reports).
	 */
	requiredRbacAction?: string;
	/**
	 * Behavioural guidance appended to the agent's system prompt whenever this
	 * package is enabled — regardless of what the admin wrote in their own
	 * prompt. Use for rules the package must ALWAYS enforce (e.g. "verify the
	 * caller before any account lookup"). Keep it short; it costs prompt budget.
	 */
	systemPromptAddendum?: string;
	/** Ordered list of configurable tiers within this package */
	tiers: Array<CapabilityTierDefinition>;
}

// ── Metadata Shape ──────────────────────────────────────────────────

/**
 * Shape of `metadata.capabilities` on an agent.
 *
 * - Key = CapabilityPackageDefinition.key (matches ToolGroup value)
 * - Value meanings:
 *   - `true`   — package enabled, all tiers use their `enabledByDefault` values
 *   - object   — package enabled, per-tier overrides (key = tier key, value = boolean)
 *   - `false`  — explicitly disabled (preserved in metadata for re-enabling)
 *   - absent   — never configured
 */
export type AgentCapabilities = Record<
	string,
	true | Record<string, boolean> | false
>;

// ── Package Registry ────────────────────────────────────────────────

/**
 * Reserved tier key for infrastructure tools that are always included
 * when the package is enabled. Tools without a capabilityTier on their
 * decorator are treated as infrastructure.
 */
export const CAPABILITY_TIER_INFRASTRUCTURE = 'infrastructure';

/**
 * All available capability packages. The frontend renders these as toggleable
 * cards in the agent editor. The backend uses them to determine which tools
 * to inject based on the agent's metadata.capabilities configuration.
 */
export const CAPABILITY_PACKAGES: Array<CapabilityPackageDefinition> = [
	{
		key: ToolGroup.EXTERNAL_CLIENT_SERVICE,
		label: 'Customer Account Service',
		description:
			'Allow callers to verify their identity and look up their invoices, orders, and account information.',
		faIcon: 'headset',
		assignableTo: 'both',
		systemPromptAddendum: `You are speaking with an external customer. Account-specific information — invoices, orders, balances, payment status — is private and requires identity verification.

- If the caller asks for ANY account-specific information and has not already been verified on this call, verify them FIRST: collect their company name plus one strong identifier (an invoice number, order number, account number, or an invoice total), then call Verify Client Identity. Do NOT call an account lookup tool for an unverified caller and wait for it to fail — verify proactively.
- A company name alone, or a phone number alone, is never enough — always collect a strong identifier.
- The moment verification succeeds, CONFIRM the account back to the caller by name (e.g. "Thanks — I've got you verified on the [company] account") before continuing, so a wrong match is caught right away.
- For "do I have any open invoices", "what's my balance", or "how much do I owe", use the account summary (a count and total), not a full invoice list. Keep spoken answers short and never read a long list of invoices aloud.`,
		tiers: [
			{
				key: 'lookups',
				label: 'Look up account information',
				description:
					'Invoices, orders, account balance, and payment status for verified callers.',
				enabledByDefault: true,
			},
			{
				key: 'payments',
				label: 'Make payments',
				description:
					'Let verified callers pay an invoice using a payment method already on file. No card or bank details are ever taken over the phone — only saved methods can be used.',
				// OPT-IN, OFF by default: money movement is high-risk, so the admin
				// must deliberately enable it on a customer-facing agent.
				enabledByDefault: false,
				systemPromptAddendum: `If the caller wants to PAY an invoice:
- First list their saved payment methods and let them choose — read each one as its brand/type and last four digits (e.g. "a Visa ending 4242"). NEVER ask for or accept a card number, security code, expiration, or bank account/routing number over the phone; only methods already on file can be used.
- Default the amount to the invoice's full outstanding balance unless the caller asks to pay a different amount.
- Before charging, read back the exact amount, which invoice, and the chosen method (brand + last four) and get an explicit "yes" from the caller. Only then make the payment — never charge without that confirmation.
- If a payment fails, tell the caller plainly and offer to try a different method on file.`,
			},
			// Future tier:
			// { key: 'orderCreation', label: 'Place orders', ... enabledByDefault: false },
		],
	},
	{
		key: ToolGroup.REPORTING,
		label: 'Report Building',
		description:
			'Build and run custom data reports — SQL-backed tables, charts, and metric cards — and render dynamic views.',
		faIcon: 'chart-line',
		// SYSTEM-ONLY. The report tools run raw SELECT SQL across tenant data with
		// no per-rep row scoping — handing that to a user-built (potentially
		// customer-facing) agent is a data-exposure risk. So it is NOT assignable
		// to user agents: hidden from the builder AND rejected at the execution
		// gate for any non-system agent. It reaches only the system Sales Helper
		// (via its `groups`), still gated per-user by the RBAC action below.
		assignableTo: 'system',
		requiredRbacAction: RbacActions.AI_AGENTS.REPORTING_TOOLS.USE.action,
		tiers: [],
	},
	{
		key: ToolGroup.API_ACTIONS,
		label: 'API Actions',
		description:
			'Let the agent call CRM API endpoints generically (discover a capability index, then execute actions). Each call runs through the normal API permission checks.',
		faIcon: 'plug',
		// SYSTEM-ONLY for now — generic API access is too broad to expose on
		// user-built agents in the current editor. Hidden from the builder and
		// gated to system agents at runtime.
		assignableTo: 'system',
		tiers: [],
	},
	{
		key: ToolGroup.SALES_HELPER,
		label: 'Sales Helper Toolkit',
		description:
			'The internal CRM toolkit (client / order / quote / invoice / task / opportunity lookups and management) used by the system Sales Helper agent.',
		faIcon: 'briefcase',
		// Internal toolkit — not offered to user-created agents.
		assignableTo: 'system',
		tiers: [],
	},
];

/**
 * Central tool-group membership for groups whose member tools do NOT self-tag
 * via the @AiAgentTool `toolGroup` field. A group's runtime membership is the
 * UNION of (tools whose decorator `toolGroup` === key) and the toolTypes listed
 * here.
 *
 * SALES_HELPER's members are otherwise-ungrouped first-class CRM tool handlers,
 * so they are curated here. REPORTING / API_ACTIONS / EXTERNAL_CLIENT_SERVICE
 * self-tag on their decorators and need no manifest entry.
 *
 * NOTE: `render_view` is intentionally absent — it self-tags REPORTING and
 * reaches the Sales Helper via the REPORTING group, not this toolkit.
 */
export const TOOL_GROUP_MEMBERSHIP: Partial<Record<ToolGroup, Array<string>>> =
	{
		[ToolGroup.SALES_HELPER]: [
			// Lookups / navigation
			'navigate',
			'clientSearch',
			'clientSummary',
			'clientAnalytics',
			'clientLifecycleStagesList',
			'productSalesSummary',
			'salesTotal',
			'cartGet',
			'orderSearch',
			'orderDetail',
			'invoiceSearch',
			'itemSearch',
			'itemDetail',
			'quoteSearch',
			'quoteSummary',
			'taskSearch',
			'userSearch',
			'dayPlanner',
			// NOTE: vendorSearch / purchaseOrderSearch / purchaseOrderDetail were
			// removed 2026-06 — they are purchasing-persona tools, not sales-rep
			// tools, and every extra tool costs routing accuracy on the mini
			// model. Re-add deliberately (or via a separate purchasing package)
			// if a buyer-facing assistant is built.
			'paymentCalculateSummary',
			'paymentTransactionHistory',
			// Client management
			'clientCreate',
			'clientUpdate',
			'clientNoteCreate',
			'clientNoteUpdate',
			'clientNoteDelete',
			'clientNoteSearch',
			'clientVisitCreate',
			'clientVisitSearch',
			// Cart / shopping
			'cartAddItem',
			'cartUpdateItem',
			'cartRemoveItem',
			'cartClear',
			'cartSetDiscount',
			// Order management
			'orderCreateNote',
			'orderNotesList',
			// Quote management
			'quoteCreate',
			'quoteUpdate',
			'quoteAddItem',
			'quoteUpdateItem',
			'quoteDelete',
			'quoteNoteCreate',
			// Email — internal-only variants (from the system noreply, to the rep
			// themselves) plus the external send that goes FROM the rep's own
			// connected mailbox to anyone (co-worker, contact, customer).
			'emailSendInternal',
			'quoteSendEmailInternal',
			'emailSendExternal',
			// Opportunity / pipeline
			'opportunityCreate',
			'opportunitySearch',
			'opportunityStagesList',
			'opportunityDetail',
			'opportunityUpdate',
			'opportunityUpdateStage',
			'opportunityCreateNote',
			'opportunityNotesList',
			// NOTE: the issue/ticket toolkit was removed from the Sales Helper
			// 2026-06 — issueSearch + issueCreate/Update/Detail/StatusUpdate, the
			// issueNote create/list/edit/delete set, and the issueWatcher trio
			// (add/list/remove). Issue management is not a sales-rep persona
			// workflow, and every extra tool costs routing accuracy on the mini
			// model. Re-add deliberately (or via a separate support package) if a
			// ticket-facing assistant is built.
			// Task management
			'taskCreate',
			'taskUpdate',
			'taskDelete',
		],
	};

// ── Helper Functions ────────────────────────────────────────────────

/** Get a package definition by its key */
export function getCapabilityPackage(
	key: string,
): CapabilityPackageDefinition | undefined {
	return CAPABILITY_PACKAGES.find((p) => p.key === key);
}

/**
 * Concatenated system-prompt guidance for every ENABLED package that declares a
 * `systemPromptAddendum`. Appended to the agent's prompt at conversation setup
 * so package behavioural rules hold regardless of the admin's own prompt.
 * Returns '' when no enabled package contributes guidance.
 */
export function getCapabilityPromptAddenda(
	capabilities: AgentCapabilities | undefined,
): string {
	if (capabilities == null) {
		return '';
	}
	const parts: Array<string> = [];
	for (const pkg of CAPABILITY_PACKAGES) {
		if (!isCapabilityPackageEnabled(capabilities, pkg.key)) {
			continue;
		}
		if (pkg.systemPromptAddendum != null) {
			parts.push(pkg.systemPromptAddendum);
		}
		// Tier-level guidance is included only when that tier is enabled (e.g. the
		// payment-confirmation flow appears only once "Make payments" is on).
		for (const tier of pkg.tiers) {
			if (
				tier.systemPromptAddendum != null &&
				isCapabilityTierEnabled(capabilities, pkg.key, tier.key)
			) {
				parts.push(tier.systemPromptAddendum);
			}
		}
	}
	return parts.join('\n\n');
}

/** Check whether a package is enabled at all (any value other than false/absent) */
export function isCapabilityPackageEnabled(
	capabilities: AgentCapabilities | undefined,
	packageKey: string,
): boolean {
	if (capabilities == null) {
		return false;
	}
	const pkg = capabilities[packageKey];
	return pkg != null && pkg !== false;
}

/**
 * Check whether a specific tier is enabled within a package.
 *
 * Resolution order:
 * 1. If package is disabled → false
 * 2. If package is `true` → use the tier's `enabledByDefault`
 * 3. If package is an object → check the tier key in the object,
 *    falling back to `enabledByDefault` if the tier key is absent
 */
export function isCapabilityTierEnabled(
	capabilities: AgentCapabilities | undefined,
	packageKey: string,
	tierKey: string,
): boolean {
	if (capabilities == null) {
		return false;
	}

	const pkg = capabilities[packageKey];

	// Package disabled or absent
	if (pkg == null || pkg === false) {
		return false;
	}

	// Package enabled with all defaults
	if (pkg === true) {
		const def = getCapabilityPackage(packageKey);
		const tier = def?.tiers.find((t) => t.key === tierKey);
		return tier?.enabledByDefault ?? false;
	}

	// Package enabled with tier overrides
	const tierVal = pkg[tierKey];
	if (tierVal != null) {
		return tierVal;
	}

	// Tier not specified in overrides — fall back to default
	const def = getCapabilityPackage(packageKey);
	const tier = def?.tiers.find((t) => t.key === tierKey);
	return tier?.enabledByDefault ?? false;
}
